import { Check, Clock, Copy, Gift } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  formatDiscountRemaining,
  useOnboardingDiscount,
} from '@/UI/GettingStarted/components/onboardingDiscount';

const COPIED_RESET_MS = 1800;

export const OnboardingDiscountBanner = () => {
  const { t } = useTranslation('getting_started');
  const { state, remainingMs, isClaimable } = useOnboardingDiscount();
  const [isCopied, setIsCopied] = useState(false);
  const copiedTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(
    () => () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    },
    [],
  );

  if (!isClaimable || !state) return null;

  const handleCopyClick = () => {
    if (isCopied) return;
    void navigator.clipboard.writeText(state.code);
    toast.success(t('discount_modal.code_copied_toast'));
    setIsCopied(true);
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setIsCopied(false), COPIED_RESET_MS);
  };

  const isUrgent = remainingMs < 30 * 60 * 1000;

  return (
    <div
      className="mb-4 flex flex-col gap-4 rounded-xl border border-border bg-card p-6 sm:flex-row sm:items-center"
      data-testid="onboarding-discount-banner"
    >
      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-lime-400">
        <Gift className="size-5 text-foreground" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold text-foreground">{t('discount_modal.title')}</h3>
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${
              isUrgent
                ? 'bg-destructive-muted text-destructive animate-pulse'
                : 'bg-muted text-foreground'
            }`}
            data-testid="onboarding-discount-banner-timer"
          >
            <Clock className="size-3" />
            {formatDiscountRemaining(remainingMs)}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">{t('discount_modal.description')}</p>
      </div>
      <button
        type="button"
        onClick={handleCopyClick}
        disabled={isCopied}
        className="group flex w-fit shrink-0 items-center gap-3 rounded-lg border border-dashed border-lime-400 bg-lime-100 px-3 py-2 text-left transition-colors hover:bg-lime-200 cursor-pointer disabled:cursor-default disabled:hover:bg-lime-100"
        data-testid="onboarding-discount-banner-copy"
      >
        <span className="font-mono text-sm font-medium tracking-wider text-lime-900">
          {state.code}
        </span>
        <span
          className={`inline-flex items-center gap-1 text-xs font-medium ${
            isCopied ? 'text-lime-800' : 'text-lime-800 group-hover:text-lime-900'
          }`}
        >
          {isCopied ? (
            <>
              <Check className="size-3.5" />
              {t('discount_modal.code_copied')}
            </>
          ) : (
            <>
              <Copy className="size-3.5" />
              {t('discount_modal.copy_code')}
            </>
          )}
        </span>
      </button>
    </div>
  );
};
